<script setup lang="ts">
  import { PropertyId } from "~/classes/entity"

const props = defineProps({
  fileId: null
})

const { state: viewModeState } = useViewMode()
const { isSelectedIn } = useSelection()
const { state: clipboardState } = useClipboard()
const { fileIdAttr } = useGlobals()
const { columnStyle, previewWidth } = useTableView()
const { getReactiveFile } = useCollectionView()

const reactiveFile = getReactiveFile(props.fileId, "collection-view")!

if (!reactiveFile)
  throw new Error("Must not instantiate CollectionTableFile without a reactive file")

const file = reactiveFile.ref()

const selected = computed(() => isSelectedIn(props.fileId, "collection-view"))
const cut = computed(() => clipboardState.value.action === "cut"
  ? clipboardState.value.files[props.fileId]
  : undefined,
)
</script>

<template>
  <div
    v-if="file"
    :[dataAttr(fileIdAttr)]="file.id"
    class="table-file"
    :class="{ selected }"
  >
    <div
      v-for="(show, idx) in viewModeState.current.show"
      :key="idx"
      class="table-header-column"
      :class="{ cut }"
      :style="columnStyle(idx)"
    >
      <div
        v-if="enumEq(show.propertyId, PropertyId.FILE_VIRTUAL_PREVIEW)"
        class="table-file-container"
        :class="{ cut }"
        :style="{
          width: px(previewWidth),
          height: px(previewWidth),
        }"
      >
        <file-thumbnail
          :file
          shadowed
          :container-width="previewWidth"
          :container-height="previewWidth"
          class="table-file-thumbnail"
        />
      </div>
      <div v-else class="table-file-property">
        <file-property-view
          :property-id="viewModeState.current.show[idx].propertyId"
          :size="viewModeState.current.show[idx].size"
          :reactive-file
          :color="viewModeState.current.show[idx].color"
          text-alignment="left"
          multiline
          :max-lines="5"
        />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.table-file
  @apply flex-none cursor-pointer flex items-center -m-1 p-1 rounded-[#{$page-table-br-file}]
  &.selected
    @apply bg-$global-blue-primary
.table-file-container
  @apply aspect-square flex items-center justify-center
.table-file-thumbnail
  @apply max-h-full max-w-full rounded-sm
.table-file-property
  @apply w-full flex

// If the file was cut before pasting
.cut
  @apply opacity-25
</style>
